import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AuditEntry {
  id: string;
  admin_id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  details: Record<string, unknown> | null;
  created_at: string;
  admin_name: string;
  admin_email: string;
  company_name: string | null;
  impersonated_user_name: string | null;
  impersonated_user_email: string | null;
  target_user_name: string | null;
  target_user_email: string | null;
  action_company_name: string | null;
  invited_by_name: string | null;
}

export interface AuditFilters {
  actions: string[];
  adminIds: string[];
  targetUserIds: string[];
  companyIds: string[];
  entityTypes: string[];
  targetRoles: string[];
  dateRanges: { from: string | null; to: string | null }[];
  page: number;
  pageSize: number;
}

export interface AuditLogResult {
  entries: AuditEntry[];
  total: number;
  page: number;
  totalPages: number;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  refetch: () => void;
}

export const DEFAULT_FILTERS: AuditFilters = {
  actions: [],
  adminIds: [],
  targetUserIds: [],
  companyIds: [],
  entityTypes: [],
  targetRoles: [],
  dateRanges: [],
  page: 1,
  pageSize: 50,
};

export function useAuditLog(filters: AuditFilters): AuditLogResult {
  const query = useQuery({
    queryKey: ["audit-log", filters],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("query_audit_logs", {
        p_actions: filters.actions.length > 0 ? filters.actions : null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        p_admin_ids: filters.adminIds.length > 0 ? (filters.adminIds as any) : null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        p_target_user_ids: filters.targetUserIds.length > 0 ? (filters.targetUserIds as any) : null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        p_company_ids: filters.companyIds.length > 0 ? (filters.companyIds as any) : null,
        p_entity_types: filters.entityTypes.length > 0 ? filters.entityTypes : null,
        p_target_roles: filters.targetRoles.length > 0 ? filters.targetRoles : null,
        p_date_ranges: filters.dateRanges.length > 0
          ? filters.dateRanges.map(r => ({
              from: r.from ? new Date(r.from + 'T00:00:00').toISOString() : null,
              to: r.to ? new Date(r.to + 'T23:59:59.999').toISOString() : null,
            }))
          : null,
        p_page: filters.page,
        p_page_size: filters.pageSize,
      });
      if (error) throw error;
      return data as unknown as {
        total: number;
        page: number;
        page_size: number;
        total_pages: number;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rows: any[];
      } | null;
    },
  });

  const result = query.data ?? null;
  const rows = result?.rows ?? [];
  const entries: AuditEntry[] = rows
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter((r: any) => r.action !== "start_impersonation")
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((r: any) => ({
      id: r.id,
      admin_id: r.admin_id,
      action: r.action,
      entity_type: r.entity_type,
      entity_id: r.entity_id,
      details: r.details ?? null,
      created_at: r.created_at,
      admin_name: r.admin_name ?? "Unknown admin",
      admin_email: r.admin_email ?? "",
      company_name: r.company_name ?? null,
      impersonated_user_name: r.impersonated_user_name ?? null,
      impersonated_user_email: r.impersonated_user_email ?? null,
      target_user_name: r.target_user_name ?? null,
      target_user_email: r.target_user_email ?? null,
      action_company_name: r.action_company_name ?? null,
      invited_by_name: r.invited_by_name ?? null,
    }));

  return {
    entries,
    total: result?.total ?? 0,
    page: result?.page ?? 1,
    totalPages: result?.total_pages ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  };
}

export const getStr = (obj: Record<string, unknown> | null, key: string): string | null => {
  if (!obj) return null;
  const v = obj[key];
  return typeof v === "string" && v.trim() ? v : null;
};

const getNum = (obj: Record<string, unknown> | null, key: string): number | null => {
  if (!obj) return null;
  const v = obj[key];
  return typeof v === "number" ? v : null;
};

export function formatAuditEntry(entry: AuditEntry): string {
  const admin = entry.admin_name;
  const target = entry.company_name ?? entry.entity_id;
  const d = entry.details;

  switch (entry.action) {
    case "suspend_company": {
      const reason = getStr(d, "suspension_reason");
      const count = getNum(d, "suspended_count");
      const type = getStr(d, "suspension_type");
      let s = `${admin} suspended ${target}`;
      if (type) s += ` (${type})`;
      if (reason) s += ` — Reason: ${reason}`;
      if (count !== null) s += ` (${count} members affected)`;
      return s;
    }
    case "unsuspend_company": {
      const count = getNum(d, "restored_count");
      return `${admin} reactivated ${target}${
        count !== null ? ` — ${count} members restored` : ""
      }`;
    }
    case "approve_company_reactivation_request": {
      const notes = getStr(d, "notes");
      let s = `${admin} approved reactivation request for ${target}`;
      if (notes) s += ` — Notes: ${notes}`;
      return s;
    }
    case "reject_company_reactivation_request": {
      const notes = getStr(d, "notes");
      let s = `${admin} rejected reactivation request for ${target}`;
      if (notes) s += ` — Notes: ${notes}`;
      return s;
    }
    case "admin_impersonate_user": {
      const targetName = getStr(d, "target_name");
      const targetEmail = getStr(d, "target_email");
      const company = entry.action_company_name;
      const nameStr = targetName && targetEmail ? `${targetName} (${targetEmail})` : targetName ?? "a user";
      let s = `${admin} started impersonating ${nameStr}`;
      if (company) s += ` at ${company}`;
      return s;
    }
    case "start_impersonation": {
      const name = entry.impersonated_user_name;
      return name
        ? `${admin} started impersonating ${name}`
        : `${admin} started an impersonation session`;
    }
    case "end_impersonation": {
      const name = entry.impersonated_user_name;
      const email = entry.impersonated_user_email;
      const nameStr = name && email ? `${name} (${email})` : name ?? null;
      return nameStr ? `${admin} ended impersonation of ${nameStr}` : `${admin} ended impersonation session`;
    }
    case "impersonate_user": {
      return `${admin} started an impersonation session`;
    }
    case "suspend_member": {
      const name = entry.target_user_name;
      const email = entry.target_user_email;
      const reason = getStr(d, "reason");
      const until = getStr(d, "suspended_until");
      const company = entry.action_company_name;
      const nameStr = name && email ? `${name} (${email})` : name ?? "a user";
      let s = `${admin} suspended ${nameStr}`;
      if (company) s += ` at ${company}`;
      if (reason) s += ` — Reason: ${reason}`;
      if (until) s += ` (until ${until})`;
      return s;
    }
    case "reactivate_member": {
      const name = entry.target_user_name;
      const email = entry.target_user_email;
      const prev = getStr(d, "previous_status");
      const company = entry.action_company_name;
      const nameStr = name && email ? `${name} (${email})` : name ?? "a user";
      let s = `${admin} reactivated ${nameStr}`;
      if (company) s += ` at ${company}`;
      if (prev) s += ` (was ${prev})`;
      return s;
    }
    case "deactivate_member": {
      const name = entry.target_user_name;
      const email = entry.target_user_email;
      const reason = getStr(d, "reason");
      const company = entry.action_company_name;
      const nameStr = name && email ? `${name} (${email})` : name ?? "a user";
      let s = `${admin} deactivated ${nameStr}`;
      if (company) s += ` at ${company}`;
      if (reason) s += ` — Reason: ${reason}`;
      return s;
    }
    case "grant_admin_role": {
      const name = entry.target_user_name;
      const email = entry.target_user_email;
      const company = entry.action_company_name;
      const nameStr = name && email ? `${name} (${email})` : name ?? "a user";
      let s = `${admin} granted admin role to ${nameStr}`;
      if (company) s += ` at ${company}`;
      return s;
    }
    case "remove_admin_role": {
      const name = entry.target_user_name;
      const email = entry.target_user_email;
      const company = entry.action_company_name;
      const nameStr = name && email ? `${name} (${email})` : name ?? "a user";
      let s = `${admin} removed admin role from ${nameStr}`;
      if (company) s += ` at ${company}`;
      return s;
    }
    case "superuser_invitation_accepted": {
      const invitedBy = entry.invited_by_name;
      let s = `A new superuser accepted an invitation`;
      if (invitedBy) s += ` (invited by ${invitedBy})`;
      return s;
    }
    case "superuser_invitation_sent": {
      const invitedEmail = getStr(d, "invited_email");
      return `${admin} sent a superuser invitation to ${invitedEmail ?? "unknown"}`;
    }
    case "activate_superuser": {
      const name = getStr(d, "target_name");
      const email = getStr(d, "target_email");
      const nameStr = name && email ? `${name} (${email})` : name ?? "a superuser";
      return `${admin} activated superuser access for ${nameStr}`;
    }
    case "deactivate_superuser": {
      const name = getStr(d, "target_name");
      const email = getStr(d, "target_email");
      const nameStr = name && email ? `${name} (${email})` : name ?? "a superuser";
      return `${admin} deactivated superuser access for ${nameStr}`;
    }
    case "remove_superuser": {
      const name = getStr(d, "target_name");
      const email = getStr(d, "target_email");
      const nameStr = name && email ? `${name} (${email})` : name ?? "a superuser";
      return `${admin} removed superuser access from ${nameStr}`;
    }
    case 'superuser_invitation_revoked': {
      const email = getStr(d, 'invited_email');
      const invitedByEmail = getStr(d, 'invited_by_email');
      const suffix = invitedByEmail ? ` (originally invited by ${invitedByEmail})` : '';
      return `${admin} revoked a superuser invitation for ${email ?? 'unknown'}${suffix}`;
    }
    case 'update_user_email': {
      const oldEmail = getStr(d, 'old_email');
      const newEmail = getStr(d, 'new_email');
      const userName = entry.target_user_name ?? oldEmail ?? 'unknown user';
      return `${admin} updated email for ${userName} from ${oldEmail ?? 'unknown'} to ${newEmail ?? 'unknown'}`;
    }
    case 'browse_category_created': {
      const name = getStr(d, 'name') ?? 'unnamed';
      const depth = getNum(d, 'depth') ?? 0;
      return `${admin} created browse category '${name}' at level ${depth + 1}`;
    }
    case 'browse_category_updated': {
      const oldName = getStr(d, 'old_name') ?? 'unknown';
      const newName = getStr(d, 'new_name') ?? oldName;
      return `${admin} updated browse category '${oldName}' → '${newName}'`;
    }
    case 'browse_category_deleted': {
      const name = getStr(d, 'name') ?? 'unknown';
      return `${admin} deleted browse category '${name}'`;
    }
    case 'system_line_item_created': {
      const name = getStr(d, 'name') ?? 'unnamed';
      return `${admin} added system item '${name}'`;
    }
    case 'system_line_item_updated': {
      const name = getStr(d, 'name') ?? 'unnamed';
      return `${admin} updated system item '${name}'`;
    }
    case 'system_line_item_deactivated': {
      const name = getStr(d, 'name') ?? 'unnamed';
      return `${admin} deactivated system item '${name}'`;
    }
    case 'system_line_item_activated': {
      const name = getStr(d, 'name') ?? 'unnamed';
      return `${admin} activated system item '${name}'`;
    }
    default:
      return `${admin} performed ${entry.action} on ${entry.entity_type}`;
  }
}

export function getActionDotColor(action: string): string {
  switch (action) {
    case "suspend_company":
      return "bg-amber-500";
    case "unsuspend_company":
    case "approve_company_reactivation_request":
      return "bg-emerald-500";
    case "reject_company_reactivation_request":
      return "bg-red-500";
    case "admin_impersonate_user":
    case "impersonate_user":
    case "start_impersonation":
      return "bg-purple-500";
    case "end_impersonation":
      return "bg-slate-400";
    case "suspend_member":
      return "bg-amber-500";
    case "reactivate_member":
      return "bg-emerald-500";
    case "deactivate_member":
      return "bg-red-500";
    case "grant_admin_role":
      return "bg-blue-500";
    case "remove_admin_role":
      return "bg-slate-400";
    case "activate_superuser":
      return "bg-emerald-500";
    case "deactivate_superuser":
      return "bg-amber-500";
    case "remove_superuser":
      return "bg-red-500";
    case "superuser_invitation_sent":
      return "bg-blue-500";
    case 'superuser_invitation_revoked':
      return 'bg-red-500';
    case 'browse_category_created':
      return 'bg-teal-500';
    case 'browse_category_updated':
      return 'bg-blue-500';
    case 'browse_category_deleted':
      return 'bg-red-500';
    case 'system_line_item_created':
      return 'bg-teal-500';
    case 'system_line_item_updated':
      return 'bg-blue-500';
    case 'system_line_item_deactivated':
      return 'bg-amber-500';
    case 'system_line_item_activated':
      return 'bg-emerald-500';
    default:
      return "bg-slate-500";
  }
}
